import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import DataGrid, { Column, FilterRow, HeaderFilter, Selection } from 'devextreme-react/data-grid';
import Form, { Item, RequiredRule } from 'devextreme-react/form';

import { AppCol } from 'components/appComponents';
import AppFiledBox from 'components/appComponents/app-field-box';
import ModalFooter from 'components/appModalFooter';
import { Popup, SelectBox, TabPanel, ValidationGroup, Validator } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';
import $createStore from 'utils/create-store-instance';

import { FactoryDetail } from 'bus/factory/types';
import { useLazyGetMethodOfReceptionListQuery } from 'bus/methodOfReception/services';
import { MethodOfReceptionItem } from 'bus/methodOfReception/types';
import { useGetProfileInfoQuery } from 'bus/profile/services/services';
import { RequestWorkForm } from 'bus/request/types';

const itemTitleRender = (tab: { name: string; index: number }) => {
  return <span>{tab.name}</span>;
};

type Props = {
  visible: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (data: RequestWorkForm) => void;
};

const SplitModal: FC<Props> = ({ visible, isLoading, onClose, onSubmit }) => {
  const [tabs, setTabs] = useState<Array<FactoryDetail>>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [methodOfReceptionList, setMethodOfReceptionList] = useState<MethodOfReceptionItem[]>([]);

  let validationRef = useRef<ValidationGroup>(null);
  const { data: profileInfoQuery } = useGetProfileInfoQuery();

  const [form, setForm] = useState<RequestWorkForm>({
    methodOfReceptionId: null,
    requestId: null,
  });

  const [getMethodOfReceptionList] = useLazyGetMethodOfReceptionListQuery();

  const factoryDataStore = useCallback(async () => {
    const defaultFilter = ['parentId', '=', null];
    const tabsData: Array<FactoryDetail> = await $createDataSource('/v1/factory', 'id', {
      pageSize: 20,
      paginate: true,
      defaultFilter,
    }).load();
    setTabs(tabsData);
    const activeTab = tabsData.findIndex((factory) => factory.id === profileInfoQuery?.factoryId);
    if (activeTab >= 0) {
      setSelectedIndex(activeTab);
    }
    //getMaterialBalanceByFactory({ id: tabsData[0].id, dateBegin, dateEnd });
  }, [profileInfoQuery?.factoryId]);

  useEffect(() => {
    factoryDataStore();
  }, [factoryDataStore]);

  const requestDateSource: any = useMemo(() => {
    const factoryId = tabs[selectedIndex]?.id || 0;
    return $createStore(`/v1/Request/factoryRequests?factoryId=${factoryId}`, 'id');
  }, [tabs, selectedIndex]);

  const onSelectionChanged = ({ selectedRowsData }: any) => {
    const data = selectedRowsData[0];
    data && data.id && setForm((prev) => ({ ...prev, requestId: data.id, methodOfReceptionId: null }));
    data && data.id && getMethodOfReceptionList({ requestId: data.id }).unwrap().then(setMethodOfReceptionList);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();
    if (validationRef.current?.instance.validate().isValid) {
      e.preventDefault && e.preventDefault();
      onSubmit(form);
    }
  };

  const changeTabHandler = async (activeIndex: number) => {
    setSelectedIndex(activeIndex);
    //await getMaterialBalanceByFactory({ id: tabs[activeIndex].id!, dateBegin, dateEnd });
  };

  return (
    <div>
      <Popup
        visible={visible}
        onHiding={onClose}
        dragEnabled={true}
        closeOnOutsideClick={false}
        showTitle={true}
        title="Перенос"
        height={650}
        width={1400}
        onHidden={onClose}
      >
        <div className="modal-block">
          <div className="modal-block__content">
            <form onSubmit={handleSubmit}>
              <ValidationGroup ref={validationRef}>
                <Form id="report" formData={form}>
                  <Item colSpan={12} colCount={12} itemType="group">
                    <TabPanel
                      height="auto"
                      dataSource={tabs}
                      selectedIndex={selectedIndex}
                      onOptionChanged={(e) => {
                        if (e.name === 'selectedIndex') {
                          changeTabHandler(e.value);
                        }
                      }}
                      animationEnabled={false}
                      itemTitleRender={itemTitleRender}
                    />

                    <DataGrid
                      dataSource={requestDateSource}
                      showBorders={true}
                      keyExpr="ID"
                      onSelectionChanged={onSelectionChanged}
                      hoverStateEnabled
                      focusedRowEnabled
                      height={400}
                      remoteOperations={true}
                    >
                      <FilterRow visible={true} />
                      <HeaderFilter visible />
                      <Selection mode="single" />
                      <Column
                        dataField="requestStatus"
                        caption=""
                        allowFiltering={false}
                        width={70}
                        cellRender={({ data }) => (
                          <div style={{ textAlign: 'center', padding: 5, background: data.requestStatus.color }}>
                            {data.requestStatus.description}
                          </div>
                        )}
                      />
                      <Column dataField="id" caption="Номер" width={100} />
                      <Column dataField="date" caption="Дата" dataType="datetime" width={150} />
                      <Column dataField="object" caption="Объект" />
                      <Column dataField="address" caption="Адрес" />
                      <Column dataField="nomenclatureName" caption="Марка" width={90} />
                      <Column dataField="density" caption="Плотность" width={70} />
                      <Column dataField="quantity" caption="Объём" width={70} />
                      <Column dataField="shipped" caption="Отлито" width={70} />
                      <Column
                        dataField="customColumn"
                        caption="Осталось"
                        width={70}
                        cellRender={({ data }) => <>{data.quantity - data.shipped}</>}
                      />
                      <Column dataField="comment" caption="Комментарий" />
                    </DataGrid>
                    <AppCol col={4}>
                      <AppFiledBox label="Метод приёма" labelTop>
                        <SelectBox
                          stylingMode="underlined"
                          placeholder="Выберите метод приёма"
                          mode="text"
                          value={form.methodOfReceptionId}
                          onValueChange={(e) => {
                            setForm((prev) => ({ ...prev, methodOfReceptionId: e }));
                          }}
                          searchMode="contains"
                          searchExpr="displayName"
                          searchEnabled
                          displayExpr="displayName"
                          valueExpr="id"
                          dataSource={methodOfReceptionList}
                        >
                          <Validator>
                            <RequiredRule message="Выберите метод приёма" />
                          </Validator>
                        </SelectBox>
                      </AppFiledBox>
                    </AppCol>
                  </Item>
                </Form>

                <ModalFooter
                  useSubmitBehavior={false}
                  handleSubmit={handleSubmit}
                  hideModal={onClose}
                  textConfirm="Перенести"
                  disabledSubmit={!form.requestId || isLoading}
                />
              </ValidationGroup>
            </form>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default SplitModal;
